import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface InventoryPrediction {
  item_id: string;
  item_name: string;
  current_stock: number;
  avg_per_turnover: number;
  turnovers_until_empty: number;
  predicted_runout_date: string | null;
  upcoming_turnovers_30d: number;
  restock_qty: number;
  threshold_count: number;
  status: "ok" | "order_soon" | "critical" | "out" | "insufficient_data";
  submission_count: number;
}

export function useInventoryPredictions(propertyId: string | undefined) {
  return useQuery({
    queryKey: ["inventory-predictions", propertyId],
    queryFn: async (): Promise<InventoryPrediction[]> => {
      if (!propertyId) return [];
      const { data, error } = await supabase.rpc("get_inventory_predictions", {
        p_property_id: propertyId,
      });
      if (error) throw error;
      return (data ?? []) as InventoryPrediction[];
    },
    enabled: !!propertyId,
    staleTime: 1000 * 60 * 5,
  });
}

export function formatRunoutLabel(prediction: InventoryPrediction): string {
  if (prediction.status === "out") return "Out of stock";
  if (prediction.status === "insufficient_data") return "Not enough data yet";
  if (!prediction.predicted_runout_date) return "Well stocked";

  const runout = new Date(prediction.predicted_runout_date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntil = Math.ceil((runout.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntil <= 0) return "Out of stock";
  if (daysUntil === 1) return "Runs out tomorrow";
  if (daysUntil <= 7) return `Runs out in ${daysUntil} days`;
  if (daysUntil <= 14) return `Runs out ~${Math.round(daysUntil / 7)} week${daysUntil > 10 ? "s" : ""}`;

  return `Runs out ${runout.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

export function statusColor(status: InventoryPrediction["status"]): string {
  switch (status) {
    case "out":
      return "text-red-600 bg-red-50 border-red-200 dark:text-red-400 dark:bg-red-950/30 dark:border-red-900";
    case "critical":
      return "text-orange-600 bg-orange-50 border-orange-200 dark:text-orange-400 dark:bg-orange-950/30 dark:border-orange-900";
    case "order_soon":
      return "text-yellow-700 bg-yellow-50 border-yellow-200 dark:text-yellow-400 dark:bg-yellow-950/30 dark:border-yellow-900";
    case "ok":
      return "text-green-700 bg-green-50 border-green-200 dark:text-green-400 dark:bg-green-950/30 dark:border-green-900";
    default:
      return "text-muted-foreground bg-muted/50 border-muted";
  }
}

export function statusDot(status: InventoryPrediction["status"]): string {
  switch (status) {
    case "out":
      return "bg-red-500";
    case "critical":
      return "bg-orange-500";
    case "order_soon":
      return "bg-yellow-500";
    case "ok":
      return "bg-green-500";
    default:
      return "bg-muted-foreground";
  }
}

export function statusLabel(status: InventoryPrediction["status"]): string {
  switch (status) {
    case "out":
      return "Out";
    case "critical":
      return "Critical";
    case "order_soon":
      return "Order Soon";
    case "ok":
      return "Stocked";
    default:
      return "No data";
  }
}

export const ATTENTION_STATUSES: InventoryPrediction["status"][] = ["out", "critical", "order_soon"];
